import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Sheet } from "../../../design-v2";

export function firstUseMotionDuration(duration) {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    ? 0
    : duration;
}

const OPTIONS = Object.freeze({
  weightUnit: [
    { value: "LB", eyebrow: "US STANDARD", primary: "lb", detail: "Pounds", label: "US Standard, pounds" },
    { value: "KG", eyebrow: "METRIC", primary: "kg", detail: "Kilograms", label: "Metric, kilograms" },
  ],
  olympicBarbellConvention: [
    { value: "TOTAL", eyebrow: "BAR TOTAL", primary: "Total", detail: "Total Weight", label: "Total Weight" },
    { value: "PER_SIDE", eyebrow: "PLATE LOADING", primary: "Per Side", detail: "Plates Per Side", label: "Plates Per Side" },
  ],
});

function copyFor(kind) {
  return kind === "weightUnit"
    ? {
        heading: "Which unit do your weights use?",
        description: "Choose the unit you normally read on dumbbells, machines and other weighted exercises.",
        groupLabel: "Weight measurement units",
      }
    : {
        heading: "How do you count barbell weight?",
        description: "Choose the way you normally think about Olympic barbell loading at the gym.",
        groupLabel: "Olympic barbell counting method",
      };
}

export default function FirstUsePreferenceSheet({
  open,
  kind,
  pending = false,
  stepCount = 1,
  stepIndex = 0,
  exiting = false,
  onChoose,
}) {
  const [selection, setSelection] = useState({ kind: null, value: null });
  const [outgoingKind, setOutgoingKind] = useState(null);
  const previousKindRef = useRef(kind);
  const transitionTimerRef = useRef(null);

  useEffect(() => {
    if (!open) setSelection({ kind: null, value: null });
  }, [open]);

  useLayoutEffect(() => {
    const previousKind = previousKindRef.current;
    clearTimeout(transitionTimerRef.current);
    if (open && stepCount > 1 && previousKind && kind && previousKind !== kind) {
      setOutgoingKind(previousKind);
      transitionTimerRef.current = setTimeout(
        () => setOutgoingKind(null),
        firstUseMotionDuration(220)
      );
    } else {
      setOutgoingKind(null);
    }
    previousKindRef.current = kind;
    return () => clearTimeout(transitionTimerRef.current);
  }, [kind, open, stepCount]);

  const currentCopy = copyFor(kind);

  function stepContent(stepKind, state = "current") {
    const copy = copyFor(stepKind);
    const options = OPTIONS[stepKind] || [];
    const selected = selection.kind === stepKind ? selection.value : null;
    const outgoing = state === "outgoing";

    return (
      <div
        className={`lz-workout-first-use-step${outgoing ? " is-leaving" : outgoingKind ? " is-entering" : ""}`}
        aria-hidden={outgoing || undefined}
      >
        <header className="lz-workout-first-use-sheet__header">
          <h2>{copy.heading}</h2>
          <p>{copy.description}</p>
        </header>
        <div
          className="lz-workout-preference-options"
          role={outgoing ? undefined : "radiogroup"}
          aria-label={outgoing ? undefined : copy.groupLabel}
        >
          {options.map((option) => {
            const checked = selected === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role={outgoing ? undefined : "radio"}
                aria-checked={outgoing ? undefined : checked}
                aria-label={outgoing ? undefined : option.label}
                className={`lz-workout-preference-option${checked ? " is-selected" : ""}`}
                disabled={pending || outgoing}
                onClick={() => setSelection({ kind: stepKind, value: option.value })}
              >
                <span className="lz-workout-preference-option__topline">
                  <span className="lz-workout-preference-option__eyebrow">{option.eyebrow}</span>
                  <span className="lz-workout-preference-option__indicator" aria-hidden="true">
                    {checked ? (
                      <svg viewBox="0 0 24 24">
                        <path d="m5 12.5 4.25 4.25L19 7" />
                      </svg>
                    ) : null}
                  </span>
                </span>
                <span className="lz-workout-preference-option__primary">{option.primary}</span>
                <span className="lz-workout-preference-option__detail">{option.detail}</span>
              </button>
            );
          })}
        </div>
        <Button
          className="lz-workout-first-use-sheet__continue"
          disabled={!selected || pending || outgoing}
          isLoading={pending && !outgoing}
          loadingLabel="Saving…"
          onClick={() => onChoose(selected)}
        >
          <span>Continue</span>
          <span aria-hidden="true">→</span>
        </Button>
        <p className="lz-workout-first-use-sheet__note">
          You can change this anytime in Workout Preferences.
        </p>
      </div>
    );
  }

  return (
    <Sheet
      open={open}
      dismissible={false}
      showCloseButton={false}
      aria-label={currentCopy.heading}
      className={`lz-workout-first-use-overlay${exiting ? " is-exiting" : ""}`}
      surfaceClassName={`lz-workout-first-use-sheet${exiting ? " is-exiting" : ""}`}
    >
      <div className="lz-workout-first-use-sheet__handle" aria-hidden="true" />
      {stepCount > 1 ? (
        <div className="lz-workout-first-use-sheet__progress" data-testid="first-use-progress" aria-hidden="true">
          {Array.from({ length: stepCount }, (_, index) => (
            <span key={index} data-active={index === stepIndex ? "true" : "false"} />
          ))}
        </div>
      ) : null}
      <div className="lz-workout-first-use-sheet__viewport">
        {outgoingKind ? stepContent(outgoingKind, "outgoing") : null}
        {stepContent(kind)}
      </div>
    </Sheet>
  );
}
